import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { trackEvent } from '../lib/analytics';

const COPY = {
  A: {
    en: {
      headline: "Don't leave without testing your Spanish level!",
      subtext: 'Book a free 30-minute 1-on-1 trial class with Cecilia (certified DELE examiner). Zero commitment!',
      cta: 'Claim My Free 30-Min Lesson',
      dismiss: "No thanks, I'll keep browsing",
      badge: 'Free Trial · DELE Examiner',
    },
    de: {
      headline: 'Gehst du schon? Teste dein Spanisch kostenlos!',
      subtext: 'Buche eine kostenlose 30-minütige Probestunde mit Cecilia (zertifizierte DELE-Prüferin). 100% unverbindlich!',
      cta: 'Jetzt kostenlose Probestunde sichern',
      dismiss: 'Nein danke, ich möchte nur weiterlesen',
      badge: 'Kostenlose Probestunde · DELE-Prüferin',
    },
  },
  B: {
    en: {
      headline: 'Get a Free Study Pack + 10% Off Your First Class!',
      subtext: 'Download a curated Spanish study pack and unlock 10% off your first 1-on-1 class with Cecilia (certified DELE examiner).',
      cta: 'Get Free Pack & Discount',
      dismiss: "No thanks, I'll keep browsing",
      badge: 'Study Pack + 10% OFF',
    },
    de: {
      headline: 'Kostenloses Lernpaket + 10% Rabatt auf deinen ersten Kurs!',
      subtext: 'Lade ein kuratiertes Spanisch-Lernpaket herunter und sichere dir 10% Rabatt auf deinen ersten Einzelunterricht mit Cecilia (zertifizierte DELE-Prüferin).',
      cta: 'Lernpaket & Rabatt sichern',
      dismiss: 'Nein danke, ich möchte nur weiterlesen',
      badge: 'Lernpaket + 10% RABATT',
    },
  },
};

const SESSION_KEY = 'exit_intent_shown_v1';
const VARIANT_KEY = 'exit_intent_variant_v1';

function detectInitialLang(siteLang) {
  // Priority: site locale if DE/EN → browser → default EN
  if (siteLang === 'de') return 'de';
  if (siteLang === 'en') return 'en';
  try {
    const nav = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
    if (nav.startsWith('de')) return 'de';
  } catch (_) {}
  return 'en';
}

function assignVariant() {
  try {
    const existing = sessionStorage.getItem(VARIANT_KEY);
    if (existing === 'A' || existing === 'B') return existing;
    const v = Math.random() < 0.5 ? 'A' : 'B';
    sessionStorage.setItem(VARIANT_KEY, v);
    return v;
  } catch (_) {
    return Math.random() < 0.5 ? 'A' : 'B';
  }
}

function trackGA(event, params) {
  try {
    if (typeof window !== 'undefined') {
      trackEvent(event, params);
    }
  } catch (_) {}
}

function isDesktopViewport() {
  if (typeof window === 'undefined') return false;
  // Skip touch-first devices — exit-intent doesn't apply
  const hasFinePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
  return hasFinePointer && window.innerWidth >= 1024;
}

export default function ExitIntentPopup() {
  const { language: siteLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(() => detectInitialLang(siteLang));
  const [variant] = useState(() => assignVariant());

  // Keep popup language in sync when the user changes the site locale
  useEffect(() => {
    if (siteLang === 'de' || siteLang === 'en') setLang(siteLang);
  }, [siteLang]);

  const trigger = useCallback(() => {
    try {
      if (sessionStorage.getItem(SESSION_KEY) === '1') return;
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch (_) {}
    setOpen(true);
  }, []);

  // GA4: fire popup_view when the modal actually becomes visible
  useEffect(() => {
    if (open) {
      trackGA('popup_view', { variant, language: lang });
    }
    // We want a single event when the modal opens (not on lang toggle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!isDesktopViewport()) return;
    // Small grace period so it never fires on the initial load bounce
    let armed = false;
    const armTimer = setTimeout(() => {
      armed = true;
    }, 4000);

    const onMouseLeave = (e) => {
      if (!armed) return;
      // Fire only when the cursor exits through the TOP of the viewport
      if (e.clientY <= 0 || (e.relatedTarget == null && e.clientY < 20)) {
        trigger();
      }
    };
    document.documentElement.addEventListener('mouseleave', onMouseLeave);
    return () => {
      clearTimeout(armTimer);
      document.documentElement.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [trigger]);

  const close = () => setOpen(false);

  const goToBooking = () => {
    trackGA('popup_click', { variant, language: lang });
    setOpen(false);
    // Scroll on landing or navigate cross-page
    if (window.location.pathname === '/') {
      const el = document.getElementById('booking');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.location.href = '/#booking';
  };

  const copy = (COPY[variant] && COPY[variant][lang]) || COPY.A.en;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
          onClick={close}
          data-testid="exit-intent-overlay"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 22, stiffness: 240 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden"
            data-testid="exit-intent-modal"
            data-variant={variant}
            role="dialog"
            aria-modal="true"
            aria-labelledby="exit-intent-title"
          >
            {/* Language toggle */}
            <div className="absolute top-4 right-14 flex items-center gap-1 rounded-full bg-gray-100 p-1 z-10">
              <button
                onClick={() => setLang('en')}
                data-testid="exit-intent-lang-en"
                aria-pressed={lang === 'en'}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${
                  lang === 'en' ? 'bg-[#4A4A4A] text-white' : 'text-gray-500 hover:text-[#4A4A4A]'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLang('de')}
                data-testid="exit-intent-lang-de"
                aria-pressed={lang === 'de'}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${
                  lang === 'de' ? 'bg-[#4A4A4A] text-white' : 'text-gray-500 hover:text-[#4A4A4A]'
                }`}
              >
                DE
              </button>
            </div>

            {/* Close button */}
            <button
              onClick={close}
              data-testid="exit-intent-close"
              aria-label="Close"
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
            >
              <X className="w-5 h-5 text-[#4A4A4A]" />
            </button>

            {/* Top gradient banner */}
            <div className="bg-gradient-to-br from-[#A8D8EA] via-[#B5DFEC] to-[#F5A6A4] px-8 pt-14 pb-10 text-center">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/90 text-[#4A4A4A] text-xs font-bold tracking-wide mb-4 shadow-sm">
                <Sparkles className="w-3.5 h-3.5 text-[#F5A6A4]" />
                {copy.badge}
              </div>
              <h2
                id="exit-intent-title"
                className="text-2xl md:text-3xl font-extrabold text-[#2F2F2F] leading-tight max-w-md mx-auto"
                data-testid="exit-intent-headline"
              >
                {copy.headline}
              </h2>
            </div>

            {/* Body */}
            <div className="px-8 py-7 text-center">
              <p className="text-[15px] leading-relaxed text-gray-700 mb-6" data-testid="exit-intent-subtext">
                {copy.subtext}
              </p>
              <button
                onClick={goToBooking}
                data-testid="exit-intent-cta"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#7DBDD9] to-[#A8D8EA] text-white font-bold text-base shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
              >
                {copy.cta}
                <ArrowRight className="w-5 h-5" />
              </button>
              <div className="mt-5">
                <button
                  onClick={close}
                  data-testid="exit-intent-dismiss"
                  className="text-sm text-gray-500 underline underline-offset-4 hover:text-[#4A4A4A] transition-colors"
                >
                  {copy.dismiss}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
